'use client';

import { ArrowLeft, EyeOff, Megaphone, MessageCircleQuestion, Phone, Plus, RefreshCw, X } from 'lucide-react';
import { useEffect } from 'react';
import styles from './consentimiento.module.css';

export type PasoConsentimiento = 'pregunta' | 'telefono';

interface ConsentimientoVoceriaModalProps {
	/** `pregunta` = la consulta; `telefono` = el acuse tras aceptar sin teléfono cargado. */
	paso: PasoConsentimiento;
	onAceptar: () => void;
	onRechazar: () => void;
	/** Cerrar SIN responder (X o Esc). Se registra como "sin responder", no como rechazo. */
	onCerrar: () => void;
	/** Cierra el acuse del paso `telefono`. */
	onListo: () => void;
	/**
	 * Modo embebido: dibuja sólo el interior (encabezado, cuerpo y pie) sin overlay
	 * ni tarjeta propios, para que el contenedor —el hub de Preferencias— ponga la
	 * tarjeta y controle su tamaño. Mismo contrato que `embedded` de `ModalShell`.
	 * Además cede el manejo de Esc: la tecla la escucha el hub, no esta pieza.
	 */
	embedded?: boolean;
	/** Si se define, muestra un botón "←" que vuelve a la vista anterior del hub. */
	onVolver?: () => void;
	/**
	 * Sólo lectura: se muestra el texto del consentimiento SIN las dos salidas que
	 * lo responden. Es como lo ve un administrador desde el perfil ajeno — puede
	 * leer a qué se le pidió consentimiento a la persona, pero la respuesta la da
	 * ella y nadie más.
	 */
	soloLectura?: boolean;
	busy?: boolean;
	error?: string | null;
}

/**
 * Diálogo del consentimiento de vocería (R62). Se monta POR ENCIMA del modal de
 * "Editar información personal" (overlay propio, mismo z-index que el de deshacer
 * cambios) la primera vez que el dueño del perfil lo abre.
 *
 * Las tres salidas NO son equivalentes y por eso hay tres handlers: autorizar,
 * no autorizar, y cerrar sin responder. Esa última se guarda como respuesta
 * propia ('sin_responder') para no volver a preguntar sin contaminar la
 * estadística: cerrar un diálogo no es negarse.
 */
export function ConsentimientoVoceriaModal({
	paso,
	onAceptar,
	onRechazar,
	onCerrar,
	onListo,
	embedded = false,
	onVolver,
	soloLectura = false,
	busy = false,
	error,
}: ConsentimientoVoceriaModalProps) {
	// Esc = cerrar solo ESTE diálogo. En captura y cortando la propagación: el
	// modal de edición escucha Esc en document, y sin esto una sola tecla cerraría
	// los dos de una.
	useEffect(() => {
		if (embedded) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			event.stopPropagation();
			if (busy) return;
			if (paso === 'telefono') onListo();
			else onCerrar();
		};
		document.addEventListener('keydown', onKey, true);
		return () => document.removeEventListener('keydown', onKey, true);
	}, [busy, embedded, paso, onCerrar, onListo]);

	const esPregunta = paso === 'pregunta';

	// El interior se arma una sola vez y se envuelve —o no— según el modo. Es el
	// mismo reparto que hace `ModalShell`: la pieza sabe dibujar su contenido, y
	// quién la muestra decide si le pone tarjeta propia o la mete en la suya.
	const contenido = (
		<>
			<header className={styles.header}>
				<div className={styles.headerLeft}>
					{onVolver ? (
						<button
							type="button"
							className={styles.backBtn}
							onClick={onVolver}
							disabled={busy}
							aria-label="Volver"
						>
							<ArrowLeft size={17} aria-hidden="true" />
						</button>
					) : null}
					<span className={styles.bar} />
					<span className={styles.headerIcon}>
						{esPregunta ? (
							<Megaphone size={16} aria-hidden="true" />
						) : (
							<Phone size={16} aria-hidden="true" />
						)}
					</span>
					<h2 className={styles.headerTitle}>
						{esPregunta ? 'Vocerías científicas UDA' : 'Agrega tu teléfono'}
					</h2>
				</div>

				{esPregunta || onVolver ? (
					<button
						type="button"
						className={styles.closeBtn}
						onClick={onCerrar}
						disabled={busy}
						aria-label={soloLectura ? 'Cerrar' : 'Cerrar sin responder'}
						title={soloLectura ? 'Cerrar' : 'Cerrar sin responder'}
					>
						<X size={18} aria-hidden="true" />
					</button>
				) : null}
			</header>

			<div className={styles.body}>
				{esPregunta ? (
					<>
						<p className={styles.text}>
							El{' '}
							<strong>
								Laboratorio de Comunicación Pública de la Ciencia</strong>{' '} de la <strong>Universidad de Atacama
							</strong>{' '}
							podrá utilizar tus datos de contacto registrados en esta plataforma solo para
							oportunidades vinculadas a tu área de especialidad: entrevistas, cuñas, notas de
							prensa, contenidos científicos, actividades de divulgación o articulación con
							actores estratégicos públicos o privados.
						</p>
						<p className={styles.question}>
							¿Autorizas que el Laboratorio te contacte cuando surja una oportunidad de este
							tipo?
						</p>
						<ul className={styles.bullets}>
							<li className={styles.bullet}>
								<MessageCircleQuestion size={12} className={styles.bulletIcon} aria-hidden="true" />
								<span>
									Autorizar no te compromete a aceptar cada solicitud. Siempre se consultará
									previamente tu disponibilidad.
								</span>
							</li>
							<li className={styles.bullet}>
								<RefreshCw size={12} className={styles.bulletIcon} aria-hidden="true" />
								<span>Se responde una sola vez y se puede cambiar cuando quieras.</span>
							</li>
						</ul>
					</>
				) : (
					<>
						<p className={styles.titulo}>¡Muchas gracias!</p>
						<p className={styles.textoAcuse}>
							Para que el Laboratorio pueda contactarte, agrega tu teléfono acá mismo, sin
							salir de esta sección:
						</p>
						<ol className={styles.pasos}>
							<li className={styles.paso}>
								<span className={styles.pasoIcono}>
									<Plus size={17} aria-hidden="true" />
								</span>
								<span className={styles.pasoTexto}>
									<span className={styles.pasoTitulo}>Abre “Agregar dato”</span>
									<span className={styles.pasoDetalle}>
										Con el botón de arriba a la derecha, o con el recuadro al final de la
										lista.
									</span>
								</span>
							</li>
							<li className={styles.paso}>
								<span className={styles.pasoIcono}>
									<Phone size={17} aria-hidden="true" />
								</span>
								<span className={styles.pasoTexto}>
									<span className={styles.pasoTitulo}>Elige “Teléfono”</span>
									<span className={styles.pasoDetalle}>
										Escribe el número donde quieras que te ubiquen y guarda.
									</span>
								</span>
							</li>
							<li className={styles.paso}>
								<span className={styles.pasoIcono}>
									<EyeOff size={17} aria-hidden="true" />
								</span>
								<span className={styles.pasoTexto}>
									<span className={styles.pasoTitulo}>Decide si se muestra</span>
									<span className={styles.pasoDetalle}>
										Oculto no aparece en tu perfil público, pero el Laboratorio sí puede verlo
										internamente.
									</span>
								</span>
							</li>
						</ol>
					</>
				)}
			</div>

			{error ? (
				<p role="alert" className={styles.error}>
					{error}
				</p>
			) : null}

			<footer className={styles.footer}>
				<div className={styles.footerActions}>
					{soloLectura ? (
						<button
							type="button"
							className={styles.btnSecondary}
							onClick={onVolver ?? onCerrar}
						>
							{onVolver ? 'Volver' : 'Cerrar'}
						</button>
					) : esPregunta ? (
						<>
							<button
								type="button"
								className={styles.btnSecondary}
								onClick={onRechazar}
								disabled={busy}
							>
								No autorizo
							</button>
							<button
								type="button"
								className={styles.btnPrimary}
								onClick={onAceptar}
								disabled={busy}
							>
								{busy ? 'Guardando…' : 'Sí, autorizo'}
							</button>
						</>
					) : (
						<button type="button" className={styles.btnPrimary} onClick={onListo}>
							Entendido
						</button>
					)}
				</div>
			</footer>
		</>
	);

	if (embedded) return contenido;

	return (
		<div
			className={styles.overlay}
			role="dialog"
			aria-modal="true"
			aria-label={esPregunta ? 'Vocerías científicas UDA' : 'Agrega tu teléfono'}
		>
			<div className={styles.card} onClick={(event) => event.stopPropagation()}>
				{contenido}
			</div>
		</div>
	);
}
